import React, { useState } from 'react';
import { DB } from '../data/database';
import { CURRENCIES, ROLES } from '../data/constants';
import { convertCurrency, getJpyRate } from '../utils/currency';
import { CheckIcon, CarIcon, EngineIcon, PartsIcon, DollarSignIcon } from './Icons';
import { logActivity } from '../data/notifications';
import { generateInvoicePDF } from '../utils/generateInvoicePDF';
import { writeAuditLog } from '../utils/audit';

const STATUS_COLORS = { draft: 'pending', sent: 'transit', viewed: 'transit', partial: 'assigned', paid: 'available', overdue: 'sold', cancelled: 'sold' };
const PAY_METHODS = ['cash', 'bank_transfer', 'cheque', 'western_union', 'crypto', 'other'];

function InvoiceDetail({ invoiceId, user, onBack }) {
  const allInvoices = DB.get('invoices') || [];
  const customers = DB.get('customers') || [];
  const [inv, setInv] = useState(() => allInvoices.find(i => i.id === invoiceId));
  const [saved, setSaved] = useState(false);
  const [showRecordPay, setShowRecordPay] = useState(false);

  if (!inv) return <div><button className="btn btn-secondary btn-sm" onClick={onBack}>← Back</button><div className="empty" style={{ marginTop: 20 }}><div className="empty-title">Invoice not found</div></div></div>;

  const customer = customers.find(c => c.id === inv.customerId);
  const isDraft = inv.status === 'draft';
  const isYardDraft = isDraft && inv.invoiceCategory === 'yard_sale';
  const invCurrency = inv.currency || 'USD';
  const custCurrency = inv.customerCurrency || customer?.currency || 'USD';
  const usdToCust = (usd) => Math.round(convertCurrency(usd, 'USD', custCurrency) * 100) / 100;

  const [lineItems, setLineItems] = useState(Array.isArray(inv.lineItems) ? inv.lineItems : []);
  const [milestones, setMilestones] = useState(Array.isArray(inv.paymentMilestones) ? inv.paymentMilestones : []);
  const [notes, setNotes] = useState(inv.notes || '');
  const [editInvNum, setEditInvNum] = useState(inv.invoiceNumber || '');
  const [editCustName, setEditCustName] = useState(inv.customerName || customer?.name || '');
  const [editCustCountry, setEditCustCountry] = useState(customer?.country || 'UAE');
  const [editCustPhone, setEditCustPhone] = useState(customer?.phone || '');
  const [validationErr, setValidationErr] = useState('');
  const [taxEnabled, setTaxEnabled] = useState(inv.tax_enabled || false);
  const [taxPct, setTaxPct] = useState(inv.tax_percentage || 5);

  const subtotal = lineItems.reduce((s, li) => s + (Number(li.fob_price) || 0), 0);
  const feesTotal = lineItems.reduce((s, li) => s + ((li.fees?.flat_charge_amount || 0) + (li.fees?.recycle_fee_amount || 0) + (li.fees?.vat_amount || 0)), 0);
  const lineTotal = lineItems.reduce((s, li) => s + (Number(li.total) || 0), 0);
  const taxAmount = taxEnabled ? Math.round(lineTotal * (taxPct / 100) * 100) / 100 : 0;
  const grandTotal = lineTotal + taxAmount;

  const getIcon = (sku) => !sku ? <DollarSignIcon /> : sku.startsWith('VEH') ? <CarIcon /> : sku.startsWith('ENG') ? <EngineIcon /> : <PartsIcon />;

  const recalcLineTotal = (li) => {
    const fob = Number(li.fob_price) || 0;
    const flat = li.fees ? Math.round(fob * (li.fees.flat_charge_pct || 0) / 100 * 100) / 100 : 0;
    const recycle = li.fees ? Math.round(fob * (li.fees.recycle_fee_pct || 0) / 100 * 100) / 100 : 0;
    const vat = li.fees ? Math.round(fob * (li.fees.vat_pct || 0) / 100 * 100) / 100 : 0;
    const fees = li.fees ? { ...li.fees, flat_charge_amount: flat, recycle_fee_amount: recycle, vat_amount: vat } : {};
    return { ...li, fees, total: Math.round((fob + flat + recycle + vat + (Number(li.freight_share) || 0) + (Number(li.cutting_charges) || 0)) * 100) / 100 };
  };

  const updateLine = (idx, field, value) => {
    setLineItems(prev => prev.map((li, i) => {
      if (i !== idx) return li;
      const updated = { ...li, [field]: value };
      if (field === 'fob_price') return recalcLineTotal(updated);
      return updated;
    }));
  };

  const updateFee = (idx, feeField, value) => {
    setLineItems(prev => prev.map((li, i) => {
      if (i !== idx) return li;
      const updated = { ...li, fees: { ...(li.fees || {}), [feeField]: Number(value) || 0 } };
      return recalcLineTotal(updated);
    }));
  };

  const addManualLine = () => {
    setLineItems(prev => [...prev, { unit_id: '', unit_sku: '', description: '', fob_price: 0, fees: {}, total: 0, manual: true }]);
  };

  const removeLine = (idx) => setLineItems(prev => prev.filter((_, i) => i !== idx));

  const updateMilestone = (idx, field, value) => {
    setMilestones(prev => prev.map((m, i) => i === idx ? { ...m, [field]: value } : m));
  };

  const saveInvoice = (newStatus) => {
    setValidationErr('');

    // G: Validate invoice number
    const finalInvNum = isYardDraft ? editInvNum.trim() : inv.invoiceNumber;
    if (!finalInvNum) { setValidationErr('Invoice number cannot be empty.'); return; }
    // Uniqueness check
    if (isYardDraft && finalInvNum !== inv.invoiceNumber) {
      const duplicate = allInvoices.find(i => i.id !== inv.id && i.invoiceNumber === finalInvNum);
      if (duplicate) { setValidationErr(`Invoice number "${finalInvNum}" already exists.`); return; }
    }

    // H: Validate line item amounts
    const hasZeroLine = lineItems.some(li => (Number(li.fob_price) || 0) <= 0 && (Number(li.total) || 0) <= 0);
    if (hasZeroLine) { setValidationErr('Line item amount must be greater than 0.'); return; }

    const total = lineItems.reduce((s, li) => s + (Number(li.total) || 0), 0);
    const updated = {
      ...inv, lineItems, notes,
      // F: Save edited yard draft fields
      ...(isYardDraft ? {
        invoiceNumber: finalInvNum,
        customerName: editCustName.trim() || inv.customerName,
        customerCountry: editCustCountry.trim() || 'UAE',
        customerPhone: editCustPhone.trim() || '',
      } : {}),
      subtotal: lineItems.reduce((s, li) => s + (Number(li.fob_price) || 0), 0),
      tax: taxAmount,
      tax_enabled: taxEnabled,
      tax_percentage: taxPct,
      total: grandTotal, grandTotal: grandTotal, balanceDue: grandTotal - (inv.amountPaid || 0),
      paymentMilestones: milestones,
      ...(newStatus ? { status: newStatus } : {}),
    };

    // FIX 6: Lock exchange rate when sending invoice
    if (newStatus === 'sent' && !inv.exchangeRateUsed) {
      const currentRate = getJpyRate(custCurrency);
      const usdRate = getJpyRate('USD');
      updated.exchangeRateUsed = currentRate / usdRate;
      updated.grandTotalCustomerCurrency = Math.round(total * updated.exchangeRateUsed * 100) / 100;
    }

    DB.set('invoices', (DB.get('invoices') || []).map(i => i.id === inv.id ? updated : i));
    setInv(updated);
    setSaved(true); setTimeout(() => setSaved(false), 2500);

    if (newStatus === 'sent') {
      logActivity.invoiceSent?.(`Invoice ${inv.invoiceNumber} is ready for payment — Total: $${total.toLocaleString()}`, inv.customerName, user.name, inv.customerId);
    }
  };

  const outstanding = (inv.total || 0) - (inv.amountPaid || 0);
  const isPaid = inv.status === 'paid';
  const canRecordPayment = !isPaid && inv.status !== 'cancelled' && outstanding > 0;
  const invPayments = Array.isArray(inv.payments) ? inv.payments : [];
  const sym = invCurrency === 'AED' ? 'AED ' : invCurrency === 'JPY' ? '¥' : '$';

  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editPayIdx, setEditPayIdx] = useState(null);

  const recomputeFromPayments = (payments) => {
    const newPaid = Math.round(payments.reduce((s, p) => s + (p.amount || 0), 0) * 100) / 100;
    const fullyPaid = newPaid >= (inv.total || 0);
    return {
      payments,
      amountPaid: newPaid,
      balanceDue: Math.round(((inv.total || 0) - newPaid) * 100) / 100,
      status: fullyPaid ? 'paid' : newPaid > 0 ? 'partial' : 'sent',
      paidDate: fullyPaid ? (payments[payments.length - 1]?.date || inv.paidDate || new Date().toISOString().split('T')[0]) : '',
    };
  };

  const saveInvoicePayments = async (updated) => {
    const allCurrent = DB.get('invoices') || [];
    const result = await DB.setAsync('invoices', allCurrent.map(i => i.id === inv.id ? updated : i));
    setSaving(false);
    if (result && !result.success) {
      console.error('[PaymentEdit] Save failed:', result.error);
      setSaveError('Save failed — please try again. (' + (result.error || 'unknown error') + ')');
      return false;
    }
    setInv(updated);
    setSaved(true); setTimeout(() => setSaved(false), 2500);
    return true;
  };

  const recordPayment = async (payAmount, payDate, payMethod, payRef) => {
    const amount = Math.round((Number(payAmount) || 0) * 100) / 100;
    if (amount <= 0) return;
    setSaving(true);
    setSaveError('');
    const payment = { amount, date: payDate, method: payMethod, reference: payRef, recordedBy: user.id, recordedAt: new Date().toISOString() };
    const payments = [...invPayments, payment];
    const recomputed = recomputeFromPayments(payments);
    const updated = { ...inv, ...recomputed };
    if (await saveInvoicePayments(updated)) {
      setShowRecordPay(false);
      logActivity.paymentVerified?.(`Payment of ${sym}${amount.toLocaleString()} recorded for ${inv.invoiceNumber}${recomputed.status === 'paid' ? ' — PAID IN FULL' : ''}`, inv.customerName, user.name, inv.customerId);
    }
  };

  const editPayment = async (idx, payAmount, payDate, payMethod, payRef) => {
    const amount = Math.round((Number(payAmount) || 0) * 100) / 100;
    if (amount <= 0) return;
    setSaving(true);
    setSaveError('');
    const oldPayment = invPayments[idx];
    const editedPayment = { ...oldPayment, amount, date: payDate, method: payMethod, reference: payRef, editedBy: user.id, editedAt: new Date().toISOString() };
    const payments = invPayments.map((p, i) => i === idx ? editedPayment : p);
    const recomputed = recomputeFromPayments(payments);
    const updated = { ...inv, ...recomputed };
    if (await saveInvoicePayments(updated)) {
      setEditPayIdx(null);
      writeAuditLog({
        action: 'payment_edited',
        entityType: 'invoice',
        entityId: inv.id,
        entityLabel: inv.invoiceNumber,
        details: { paymentIndex: idx, before: oldPayment, after: editedPayment, amountPaidBefore: inv.amountPaid, amountPaidAfter: recomputed.amountPaid },
      });
    }
  };

  const deletePayment = async (idx) => {
    if (!window.confirm(`Delete this payment of ${sym}${(invPayments[idx]?.amount || 0).toLocaleString()}? This cannot be undone.`)) return;
    setSaving(true);
    setSaveError('');
    const deletedPayment = invPayments[idx];
    const payments = invPayments.filter((_, i) => i !== idx);
    const recomputed = recomputeFromPayments(payments);
    const updated = { ...inv, ...recomputed };
    if (await saveInvoicePayments(updated)) {
      writeAuditLog({
        action: 'payment_deleted',
        entityType: 'invoice',
        entityId: inv.id,
        entityLabel: inv.invoiceNumber,
        details: { paymentIndex: idx, deleted: deletedPayment, amountPaidBefore: inv.amountPaid, amountPaidAfter: recomputed.amountPaid },
      });
    }
  };

  const printInvoice = () => {
    const bankDetails = (() => { try { return DB.get('appConfig')?.bank_details || {}; } catch { return {}; } })();
    const bankHtml = (bankDetails.bank_name || bankDetails.iban) ? `<div style="margin-top:20px;padding:12px;background:#f6f8fb;border-radius:6px;font-size:11px"><strong>Payment Details</strong><br>${bankDetails.bank_name ? 'Bank: '+bankDetails.bank_name+'<br>' : ''}${bankDetails.account_name ? 'Account: '+bankDetails.account_name+'<br>' : ''}${bankDetails.account_number ? 'A/C: '+bankDetails.account_number+'<br>' : ''}${bankDetails.iban ? 'IBAN: '+bankDetails.iban+'<br>' : ''}${bankDetails.swift_code ? 'SWIFT: '+bankDetails.swift_code+'<br>' : ''}${bankDetails.branch ? 'Branch: '+bankDetails.branch+'<br>' : ''}${bankDetails.currency ? 'Currency: '+bankDetails.currency : ''}</div>` : '';
    const rows = lineItems.map(li => `<tr><td>${li.unit_sku || '—'}</td><td>${li.description}</td><td style="text-align:right">$${(li.fob_price||0).toLocaleString()}</td><td style="text-align:right">$${((li.fees?.flat_charge_amount||0)+(li.fees?.recycle_fee_amount||0)+(li.fees?.vat_amount||0)).toLocaleString()}</td><td style="text-align:right;font-weight:700">$${(li.total||0).toLocaleString()}</td></tr>`).join('');
    const msRows = milestones.map(m => `<tr><td>${m.label}</td><td>${m.percentage||''}%</td><td style="text-align:right">$${(m.amount||0).toLocaleString()}</td><td>${m.due_date||'—'}</td><td style="font-weight:600;color:${m.status==='paid'?'green':'#c00'}">${m.status==='paid'?'Paid':'Pending'}</td></tr>`).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${inv.invoiceNumber}</title><style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:Arial,sans-serif;padding:30px;color:#111;font-size:12px}h1{font-size:22px;margin-bottom:4px}.sub{color:#666;font-size:11px;margin-bottom:20px}table{width:100%;border-collapse:collapse;margin-bottom:16px}th{background:#1a3a6b;color:#fff;text-align:left;padding:7px 10px;font-size:10px}td{padding:6px 10px;border-bottom:1px solid #e0e0e0}.total-row{font-weight:800;font-size:14px;background:#f0f0f0}@media print{body{padding:10px}}</style></head><body><h1>★ BIG STAR</h1><div class="sub">Invoice: ${inv.invoiceNumber} · ${inv.issuedDate || ''} · Status: ${inv.status}</div><p style="margin-bottom:16px"><strong>${inv.customerName}</strong>${customer?.company?' · '+customer.company:''}${customer?.country?' · '+customer.country:''}</p><table><tr><th>SKU</th><th>Description</th><th style="text-align:right">FOB</th><th style="text-align:right">Fees</th><th style="text-align:right">Total</th></tr>${rows}<tr class="total-row"><td colspan="4" style="text-align:right;padding:10px">Grand Total</td><td style="text-align:right;padding:10px">$${grandTotal.toLocaleString()}</td></tr></table>${milestones.length?`<h3 style="margin-bottom:8px">Payment Milestones</h3><table><tr><th>Milestone</th><th>%</th><th style="text-align:right">Amount</th><th>Due</th><th>Status</th></tr>${msRows}</table>`:''}${bankHtml}<script>window.onload=()=>{window.print()}<\/script></body></html>`;
    let iframe = document.getElementById('print-frame');
    if (!iframe) { iframe = document.createElement('iframe'); iframe.id = 'print-frame'; iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:none;'; document.body.appendChild(iframe); }
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open(); doc.write(html); doc.close();
    iframe.onload = () => { try { iframe.contentWindow.print(); } catch {} };
  };

  const overdueDays = (d) => { if (!d) return 0; const diff = Math.floor((Date.now() - new Date(d).getTime()) / 86400000); return diff > 0 ? diff : 0; };

  return (
    <div>
      <button className="btn btn-secondary btn-sm" onClick={onBack} style={{ marginBottom: 16 }}>← Back to Invoices</button>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
            {isYardDraft ? (
              <div>
                <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 2 }}>Invoice Number</div>
                <input className="form-input" value={editInvNum} onChange={e => setEditInvNum(e.target.value)} style={{ fontSize: 18, fontWeight: 700, width: 140, padding: '4px 8px', border: '1px solid var(--border)' }} placeholder="Inv #" />
              </div>
            ) : (
              <h2 style={{ margin: 0 }}>{inv.invoiceNumber}</h2>
            )}
            <span className={`status status-${STATUS_COLORS[inv.status] || 'pending'}`} style={{ fontSize: 10 }}>{(inv.status || '').replace(/_/g, ' ')}</span>
            {inv.invoiceCategory && <span style={{ fontSize: 9, background: 'var(--bg3)', padding: '2px 6px', borderRadius: 4, color: 'var(--text3)', textTransform: 'uppercase' }}>{inv.invoiceCategory.replace(/_/g, ' ')}</span>}
          </div>
          {isYardDraft ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
              <div>
                <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 2 }}>Customer</div>
                <input className="form-input" value={editCustName} onChange={e => setEditCustName(e.target.value)} style={{ fontSize: 12, width: 180, padding: '4px 8px' }} placeholder="Name" />
              </div>
              <div>
                <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 2 }}>Phone</div>
                <input className="form-input" value={editCustPhone} onChange={e => setEditCustPhone(e.target.value)} style={{ fontSize: 12, width: 140, padding: '4px 8px' }} placeholder="Phone" />
              </div>
              <div>
                <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 2 }}>Country</div>
                <input className="form-input" value={editCustCountry} onChange={e => setEditCustCountry(e.target.value)} style={{ fontSize: 12, width: 100, padding: '4px 8px' }} placeholder="Country" />
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--text2)' }}>
              <strong>{inv.customerName}</strong>
              {customer?.company && ` · ${customer.company}`}
              {customer?.country && ` · ${customer.country}`}
            </div>
          )}
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
            Issued: {inv.issuedDate || '—'}
            {inv.containerNumber && ` · Container: ${inv.containerNumber}`}
          </div>
          {validationErr && <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, padding: '4px 8px', background: 'rgba(248,81,73,0.08)', borderRadius: 4 }}>{validationErr}</div>}
        </div>
        {saved && <span className="save-toast"><CheckIcon />Saved!</span>}
      </div>

      {/* Line Items */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><div className="card-title">Line Items</div></div>
        <div className="card-body" style={{ padding: 0 }}>
          {lineItems.map((li, idx) => (
            <div key={idx} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{ color: 'var(--text3)', width: 18, flexShrink: 0 }}>{getIcon(li.unit_sku)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {(isDraft && li.manual) || isYardDraft ? (
                    <>
                      <input className="form-input" value={li.description} onChange={e => updateLine(idx, 'description', e.target.value)} placeholder="Description / Article" style={{ fontSize: 12, marginBottom: 4 }} />
                      {li.unit_sku && <div style={{ fontSize: 10, color: 'var(--cyan)', fontFamily: 'monospace' }}>{li.unit_sku}</div>}
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{li.description}</div>
                      <div style={{ fontSize: 10, color: 'var(--cyan)', fontFamily: 'monospace' }}>{li.unit_sku}</div>
                    </>
                  )}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--green)' }}>{invCurrency === 'AED' ? 'AED ' : invCurrency === 'JPY' ? '¥' : '$'}{(li.total || 0).toLocaleString()}</div>
                </div>
                {isDraft && <button onClick={() => removeLine(idx)} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 14 }}>×</button>}
              </div>

              {/* Price breakdown — simplified for yard sales */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 11 }}>
                <div>
                  <span style={{ color: 'var(--text3)' }}>{isYardDraft || inv.invoiceCategory === 'yard_sale' ? 'Amount: ' : 'FOB: '}</span>
                  {isDraft ? <input type="number" value={li.fob_price || ''} onChange={e => updateLine(idx, 'fob_price', e.target.value)} style={{ width: 80, fontSize: 11, padding: '2px 4px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 3, color: 'var(--text)' }} /> : <span>{invCurrency === 'AED' ? 'AED ' : invCurrency === 'JPY' ? '¥' : '$'}{(li.fob_price || 0).toLocaleString()}</span>}
                </div>
                {inv.invoiceCategory !== 'yard_sale' && li.fees && (
                  <>
                    <div>
                      <span style={{ color: 'var(--text3)' }}>Flat {isDraft ? <input type="number" value={li.fees.flat_charge_pct || ''} onChange={e => updateFee(idx, 'flat_charge_pct', e.target.value)} style={{ width: 35, fontSize: 10, padding: '1px 3px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 3, color: 'var(--text)' }} /> : `${li.fees.flat_charge_pct||0}`}%: </span>
                      <span>${(li.fees.flat_charge_amount || 0).toLocaleString()}</span>
                    </div>
                    <div>
                      <span style={{ color: 'var(--text3)' }}>Recycle {isDraft ? <input type="number" value={li.fees.recycle_fee_pct || ''} onChange={e => updateFee(idx, 'recycle_fee_pct', e.target.value)} style={{ width: 35, fontSize: 10, padding: '1px 3px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 3, color: 'var(--text)' }} /> : `${li.fees.recycle_fee_pct||0}`}%: </span>
                      <span>${(li.fees.recycle_fee_amount || 0).toLocaleString()}</span>
                    </div>
                    <div>
                      <span style={{ color: 'var(--text3)' }}>VAT {isDraft ? <input type="number" value={li.fees.vat_pct || ''} onChange={e => updateFee(idx, 'vat_pct', e.target.value)} style={{ width: 35, fontSize: 10, padding: '1px 3px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 3, color: 'var(--text)' }} /> : `${li.fees.vat_pct||0}`}%: </span>
                      <span>${(li.fees.vat_amount || 0).toLocaleString()}</span>
                    </div>
                  </>
                )}
                {inv.invoiceCategory !== 'yard_sale' && li.freight_share > 0 && <div><span style={{ color: 'var(--text3)' }}>Freight: </span>${li.freight_share}</div>}
                {inv.invoiceCategory !== 'yard_sale' && li.cutting_charges > 0 && <div><span style={{ color: 'var(--text3)' }}>Cutting: </span>${li.cutting_charges}</div>}
              </div>
            </div>
          ))}
          {isDraft && (
            <div style={{ padding: '10px 16px' }}>
              <button className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={addManualLine}>+ Add Line Item</button>
            </div>
          )}
        </div>
      </div>

      {/* Totals */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ padding: '14px 20px' }}>
          {inv.invoiceCategory === 'yard_sale' ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}><span style={{ color: 'var(--text2)' }}>Subtotal</span><span>{invCurrency === 'AED' ? 'AED ' : invCurrency === 'JPY' ? '¥' : '$'}{lineTotal.toLocaleString()}</span></div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}><span style={{ color: 'var(--text2)' }}>Subtotal (FOB)</span><span>${subtotal.toLocaleString()}</span></div>
              {feesTotal > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}><span style={{ color: 'var(--text2)' }}>Fees</span><span>${feesTotal.toLocaleString()}</span></div>}
            </>
          )}

          {/* Tax toggle */}
          {isDraft ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, marginBottom: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: 'var(--text2)' }}>
                  <input type="checkbox" checked={taxEnabled} onChange={e => setTaxEnabled(e.target.checked)} style={{ accentColor: 'var(--green)' }} />
                  Tax
                </label>
                {taxEnabled && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input type="number" value={taxPct} onChange={e => setTaxPct(Number(e.target.value) || 0)} style={{ width: 45, fontSize: 12, padding: '2px 4px', textAlign: 'right', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 3, color: 'var(--text)' }} />
                    <span style={{ color: 'var(--text3)', fontSize: 11 }}>%</span>
                  </div>
                )}
              </div>
              <span>{taxEnabled ? `${invCurrency === 'AED' ? 'AED ' : invCurrency === 'JPY' ? '¥' : '$'}${taxAmount.toLocaleString()}` : '—'}</span>
            </div>
          ) : (
            taxEnabled && taxAmount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                <span style={{ color: 'var(--text2)' }}>Tax ({taxPct}%)</span>
                <span>{invCurrency === 'AED' ? 'AED ' : invCurrency === 'JPY' ? '¥' : '$'}{taxAmount.toLocaleString()}</span>
              </div>
            )
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 800, paddingTop: 8, borderTop: '2px solid var(--border)' }}><span>Grand Total ({invCurrency})</span><span style={{ color: 'var(--green)' }}>{invCurrency === 'AED' ? 'AED ' : invCurrency === 'JPY' ? '¥' : '$'}{grandTotal.toLocaleString()}</span></div>
          {custCurrency !== invCurrency && custCurrency !== 'USD' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--blue)', marginTop: 4 }}>
                <span>In {custCurrency}</span>
                <span>{CURRENCIES[custCurrency]?.symbol || ''}{inv.exchangeRateUsed ? Math.round(grandTotal * inv.exchangeRateUsed * 100) / 100 : usdToCust(grandTotal)}</span>
              </div>
              {inv.exchangeRateUsed && (
                <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>
                  Exchange rate locked at send date: 1 USD = {inv.exchangeRateUsed.toFixed(4)} {custCurrency}
                </div>
              )}
              {!inv.exchangeRateUsed && isDraft && (
                <div style={{ fontSize: 10, color: 'var(--yellow)', marginTop: 2 }}>
                  Using live rate — will be locked when sent
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Payment Status */}
      {(invPayments.length > 0 || !isDraft) && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><div className="card-title">Payment Status</div></div>
          <div className="card-body" style={{ padding: '14px 20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}><span style={{ color: 'var(--text2)' }}>Total</span><span style={{ fontWeight: 700 }}>{sym}{(inv.total || 0).toLocaleString()}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}><span style={{ color: 'var(--text2)' }}>Received</span><span style={{ fontWeight: 700, color: 'var(--green)' }}>{sym}{(inv.amountPaid || 0).toLocaleString()}</span></div>
            {outstanding > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}><span style={{ color: 'var(--text2)' }}>Outstanding</span><span style={{ fontWeight: 700, color: 'var(--yellow)' }}>{sym}{outstanding.toLocaleString()}</span></div>}
            {(inv.total || 0) > 0 && (
              <div style={{ marginTop: 6 }}>
                <div style={{ height: 6, background: 'var(--bg3)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.min(100, Math.round((inv.amountPaid || 0) / (inv.total || 1) * 100))}%`, background: isPaid ? 'var(--green)' : 'var(--blue)', borderRadius: 3, transition: 'width 0.3s' }} />
                </div>
                <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>{Math.round((inv.amountPaid || 0) / (inv.total || 1) * 100)}% paid</div>
              </div>
            )}
            {invPayments.length > 0 && (
              <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 6 }}>RECORDED PAYMENTS</div>
                {invPayments.map((p, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: i < invPayments.length - 1 ? '1px solid var(--border)' : 'none', fontSize: 12 }}>
                    <div>
                      <span style={{ fontWeight: 600, color: 'var(--green)' }}>{sym}{(p.amount || 0).toLocaleString()}</span>
                      <span style={{ color: 'var(--text3)', marginLeft: 8 }}>{(p.method || '').replace(/_/g, ' ')}</span>
                      {p.reference && <span style={{ color: 'var(--text3)', marginLeft: 8 }}>Ref: {p.reference}</span>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: 'var(--text3)', fontSize: 11 }}>{p.date || ''}</span>
                      {inv.status !== 'cancelled' && (
                        <>
                          <button onClick={() => { setSaveError(''); setEditPayIdx(i); }} disabled={saving}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--blue)', fontSize: 11, padding: '2px 4px', fontWeight: 600 }}>Edit</button>
                          <button onClick={() => deletePayment(i)} disabled={saving}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', fontSize: 11, padding: '2px 4px', fontWeight: 600 }}>Del</button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Payment Milestones */}
      {milestones.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><div className="card-title">Payment Milestones</div></div>
          <div className="card-body" style={{ padding: 0 }}>
            {milestones.map((m, i) => {
              const od = overdueDays(m.due_date);
              return (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: i < milestones.length - 1 ? '1px solid var(--border)' : 'none', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    {isDraft ? (
                      <input className="form-input" value={m.label} onChange={e => updateMilestone(i, 'label', e.target.value)} style={{ fontSize: 12, padding: '4px 8px', marginBottom: 4 }} />
                    ) : (
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{m.label}</div>
                    )}
                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>
                      Due: {isDraft ? <input type="date" value={m.due_date || ''} onChange={e => updateMilestone(i, 'due_date', e.target.value)} style={{ fontSize: 10, padding: '2px 4px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 3, color: 'var(--text)' }} /> : (m.due_date || '—')}
                      {m.paid_date && ` · Paid: ${m.paid_date}`}
                      {m.status !== 'paid' && od > 0 && <span style={{ color: 'var(--red)', marginLeft: 6 }}>{od}d overdue</span>}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    {isDraft ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ fontSize: 10, color: 'var(--text3)' }}>$</span>
                        <input type="number" value={m.amount || ''} onChange={e => updateMilestone(i, 'amount', Number(e.target.value) || 0)} style={{ width: 70, fontSize: 12, padding: '3px 6px', textAlign: 'right', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 3, color: 'var(--text)' }} />
                      </div>
                    ) : (
                      <div style={{ fontSize: 14, fontWeight: 700 }}>${(m.amount || 0).toLocaleString()}</div>
                    )}
                    <span style={{ fontSize: 9, fontWeight: 700, color: m.status === 'paid' ? 'var(--green)' : od > 0 ? 'var(--red)' : 'var(--yellow)' }}>
                      {m.status === 'paid' ? '✓ Paid' : od > 0 ? 'Overdue' : 'Pending'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bank Details */}
      {(() => {
        const bankDetails = (() => { try { const cfg = DB.get('appConfig'); return cfg?.bank_details || {}; } catch { return {}; } })();
        const hasBank = bankDetails.bank_name || bankDetails.account_number || bankDetails.iban;
        if (!hasBank) return null;
        return (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header"><div className="card-title" style={{ fontSize: 12 }}>Payment Details</div></div>
            <div className="card-body" style={{ padding: '10px 16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 6, fontSize: 12 }}>
                {bankDetails.bank_name && <div><span style={{ color: 'var(--text3)' }}>Bank: </span>{bankDetails.bank_name}</div>}
                {bankDetails.account_name && <div><span style={{ color: 'var(--text3)' }}>Account: </span>{bankDetails.account_name}</div>}
                {bankDetails.account_number && <div><span style={{ color: 'var(--text3)' }}>A/C No: </span>{bankDetails.account_number}</div>}
                {bankDetails.iban && <div><span style={{ color: 'var(--text3)' }}>IBAN: </span>{bankDetails.iban}</div>}
                {bankDetails.swift_code && <div><span style={{ color: 'var(--text3)' }}>SWIFT: </span>{bankDetails.swift_code}</div>}
                {bankDetails.branch && <div><span style={{ color: 'var(--text3)' }}>Branch: </span>{bankDetails.branch}</div>}
                {bankDetails.currency && <div><span style={{ color: 'var(--text3)' }}>Currency: </span>{bankDetails.currency}</div>}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Notes */}
      <div className="form-group" style={{ marginBottom: 16 }}>
        <label className="form-label">Notes</label>
        <textarea className="form-input" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Internal or customer-facing notes…" style={{ resize: 'vertical' }} />
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button className="btn btn-secondary" onClick={onBack}>← Back</button>
        {isDraft && <button className="btn btn-primary" onClick={() => saveInvoice()}>Save Draft</button>}
        {isDraft && <button className="btn btn-success" onClick={() => saveInvoice('sent')}><CheckIcon />Send to Customer</button>}
        {canRecordPayment && (
          <button className="btn btn-success" onClick={() => setShowRecordPay(true)}>
            <DollarSignIcon />Record Payment
          </button>
        )}
        {(inv.status === 'draft' || inv.status === 'sent') && (
          <button className="btn btn-secondary" style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
            onClick={() => { if (window.confirm('Cancel this invoice?')) saveInvoice('cancelled'); }}>Cancel Invoice</button>
        )}
        <button className="btn btn-secondary" onClick={async () => {
          try {
            await generateInvoicePDF(inv);
          } catch (err) {
            console.error('PDF generation failed:', err?.message, err?.stack, err);
            alert('PDF generation failed:\n\n' + (err?.message || 'Unknown error') + '\n\nCheck browser console for details.');
          }
        }}>📄 Download PDF</button>
        {!isDraft && inv.invoiceCategory !== 'credit_note' && inv.invoiceCategory !== 'debit_note' && (
          <button className="btn btn-secondary" style={{ color: 'var(--cyan)', borderColor: 'var(--cyan)' }}
            onClick={() => createCreditNote(inv)}>
            Credit Note
          </button>
        )}
      </div>

      {/* Record Payment Modal */}
      {showRecordPay && <RecordPaymentModal
        outstanding={outstanding}
        currency={sym}
        saving={saving}
        saveError={saveError}
        onSubmit={recordPayment}
        onClose={() => { if (!saving) { setShowRecordPay(false); setSaveError(''); } }}
      />}
      {/* Edit Payment Modal */}
      {editPayIdx !== null && invPayments[editPayIdx] && <RecordPaymentModal
        outstanding={outstanding + (invPayments[editPayIdx].amount || 0)}
        currency={sym}
        saving={saving}
        saveError={saveError}
        editMode
        initialValues={invPayments[editPayIdx]}
        onSubmit={(amt, dt, meth, ref) => editPayment(editPayIdx, amt, dt, meth, ref)}
        onClose={() => { if (!saving) { setEditPayIdx(null); setSaveError(''); } }}
      />}
    </div>
  );
}

function RecordPaymentModal({ outstanding, currency, saving, saveError, onSubmit, onClose, editMode, initialValues }) {
  const [amount, setAmount] = useState(editMode && initialValues ? String(initialValues.amount || '') : String(outstanding));
  const [date, setDate] = useState(editMode && initialValues ? (initialValues.date || '') : new Date().toISOString().split('T')[0]);
  const [method, setMethod] = useState(editMode && initialValues ? (initialValues.method || 'cash') : 'cash');
  const [reference, setReference] = useState(editMode && initialValues ? (initialValues.reference || '') : '');
  const parsedAmount = Number(amount) || 0;

  return (
    <div className="modal-overlay" onClick={saving ? undefined : onClose}>
      <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">{editMode ? 'Edit Payment' : 'Record Payment'}</h3>
          <button className="modal-close" onClick={onClose} disabled={saving}>×</button>
        </div>
        <div className="modal-body">
          {saveError && (
            <div style={{ padding: '8px 12px', background: 'rgba(248,81,73,0.1)', border: '1px solid rgba(248,81,73,0.3)', borderRadius: 6, fontSize: 12, color: 'var(--red)', marginBottom: 12 }}>
              {saveError}
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Amount <span className="req">*</span></label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text3)' }}>{currency}</span>
              <input type="number" className="form-input" value={amount} onChange={e => setAmount(e.target.value)}
                style={{ fontSize: 16, fontWeight: 700 }} autoFocus disabled={saving} />
            </div>
            {parsedAmount !== outstanding && outstanding > 0 && (
              <button type="button" onClick={() => setAmount(String(outstanding))} disabled={saving}
                style={{ fontSize: 10, color: 'var(--blue)', background: 'none', border: 'none', cursor: 'pointer', marginTop: 4, padding: 0 }}>
                Fill outstanding: {currency}{outstanding.toLocaleString()}
              </button>
            )}
          </div>
          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="form-group">
              <label className="form-label">Date</label>
              <input type="date" className="form-input" value={date} onChange={e => setDate(e.target.value)} disabled={saving} />
            </div>
            <div className="form-group">
              <label className="form-label">Method</label>
              <select className="form-select" value={method} onChange={e => setMethod(e.target.value)} disabled={saving}>
                {PAY_METHODS.map(m => <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Reference / Note (optional)</label>
            <input className="form-input" value={reference} onChange={e => setReference(e.target.value)}
              placeholder="Receipt #, transaction ref, etc." disabled={saving} />
          </div>
          {parsedAmount > outstanding && outstanding > 0 && (
            <div style={{ fontSize: 11, color: 'var(--yellow)', marginTop: 4 }}>
              Amount exceeds outstanding balance ({currency}{outstanding.toLocaleString()})
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-success" disabled={parsedAmount <= 0 || saving}
            onClick={() => onSubmit(amount, date, method, reference.trim())}>
            {saving ? 'Saving...' : <><CheckIcon />{editMode ? 'Save Changes' : parsedAmount >= outstanding && outstanding > 0 ? 'Mark Paid' : `Record ${currency}${parsedAmount.toLocaleString()}`}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function createCreditNote(originalInvoice) {
  const invoices = DB.get('invoices') || [];
  const d = new Date();
  const prefix = `CN-${String(d.getFullYear()).slice(2)}${String(d.getMonth() + 1).padStart(2, '0')}`;
  const existing = invoices.filter(i => i.invoiceNumber?.startsWith(prefix));
  const invoiceNumber = `${prefix}-${String(existing.length + 1).padStart(3, '0')}`;

  const creditNote = {
    id: DB.genId('CN'),
    invoiceNumber,
    customerId: originalInvoice.customerId,
    customerName: originalInvoice.customerName,
    invoiceType: 'adjustment',
    invoiceCategory: 'credit_note',
    linkedInvoiceId: originalInvoice.id,
    lineItems: [{ description: `Credit for ${originalInvoice.invoiceNumber}`, fob_price: 0, amount: 0, total: 0, fees: {} }],
    subtotal: 0,
    tax: 0,
    total: 0,
    currency: originalInvoice.currency || 'USD',
    customerCurrency: originalInvoice.customerCurrency || 'USD',
    status: 'draft',
    issuedDate: new Date().toISOString().split('T')[0],
    amountPaid: 0,
    balanceDue: 0,
    paymentMilestones: [],
    notes: `Credit note against invoice ${originalInvoice.invoiceNumber}`,
    createdAt: new Date().toISOString().split('T')[0],
  };

  DB.set('invoices', [...invoices, creditNote]);
  alert(`Credit Note ${invoiceNumber} created as draft. Edit it to set the credit amount.`);
}

export default InvoiceDetail;
